import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSearch } from '@tanstack/react-router';
import { Bolt, Copy, SlidersHorizontal } from 'lucide-react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import {
  AutocompleteSingleSelect,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Form,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  toast,
} from '@shigo-ui/ui';

import { showRequestError } from '../../../../../lib/requestErrorToast';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import { ShigoPermission } from '../../../../auth/permissions';
import { dashboardT } from '../../../../../lib/messages';
import {
  endpointGeneratorFormSchema,
  type EndpointGeneratorFormValue,
} from '../../endpointGeneratorFormSchema';
import { endpointGeneratorRouteSearchSchema } from '../../endpointGeneratorRouteSearch';
import {
  useEndpointMacros,
  useEndpointPlacementOptions,
  useEndpointPublisherOptions,
  useGenerateEndpointMutation,
} from '../../hooks';
import {
  EndpointAdServerType,
  EndpointIntegrationType,
  EndpointProjection,
  EndpointRegion,
  endpointLookupLabel,
  projectGeneratedEndpoint,
  type EndpointLookupOption,
} from '../../model';
import { EndpointMacroDialog } from '../EndpointMacroDialog/EndpointMacroDialog';
import { copyToClipboard } from '../../../../../lib/clipboard';

const adServerLabels: Record<EndpointAdServerType, string> = {
  [EndpointAdServerType.Exchange]: 'Exchange',
  [EndpointAdServerType.FreeWheel]: 'FreeWheel',
  [EndpointAdServerType.Gam]: 'GAM',
  [EndpointAdServerType.Publica]: 'Publica',
  [EndpointAdServerType.Roku]: 'Roku',
  [EndpointAdServerType.SpringServe]: 'SpringServe',
};

const projectionLabelKeys = {
  [EndpointProjection.All]: 'endpointGenerator.projection.ALL',
  [EndpointProjection.Optimum]: 'endpointGenerator.projection.OPTIMUM',
  [EndpointProjection.Minimum]: 'endpointGenerator.projection.MIN',
} as const;

export function EndpointGeneratorPage() {
  'use no memo';

  const search = endpointGeneratorRouteSearchSchema(useSearch({ strict: false }));
  const form = useForm<EndpointGeneratorFormValue>({
    resolver: zodResolver(endpointGeneratorFormSchema),
    defaultValues: {
      publisherId: search.publisherId ?? '',
      placementId: search.placementId ?? '',
      adServerType: EndpointAdServerType.Exchange,
      integrationType: EndpointIntegrationType.Vast,
      region: EndpointRegion.Us,
      audio: false,
    },
  });
  const publisherId = useWatch({ control: form.control, name: 'publisherId' });
  const placementId = useWatch({ control: form.control, name: 'placementId' });
  const adServerType = useWatch({ control: form.control, name: 'adServerType' });
  const integrationType = useWatch({ control: form.control, name: 'integrationType' });
  const region = useWatch({ control: form.control, name: 'region' });
  const deliveryType = useWatch({ control: form.control, name: 'deliveryType' });
  const [publisherSearch, setPublisherSearch] = React.useState('');
  const [placementSearch, setPlacementSearch] = React.useState('');
  const [selectedPublisher, setSelectedPublisher] = React.useState<EndpointLookupOption>();
  const [selectedPlacement, setSelectedPlacement] = React.useState<EndpointLookupOption>();
  const [fullEndpoint, setFullEndpoint] = React.useState('');
  const [projection, setProjection] = React.useState(EndpointProjection.Minimum);
  const [macroDialogOpen, setMacroDialogOpen] = React.useState(false);
  const publishers = useEndpointPublisherOptions(publisherSearch, publisherId);
  const placements = useEndpointPlacementOptions(placementSearch, publisherId, placementId);
  const macros = useEndpointMacros(adServerType, integrationType === EndpointIntegrationType.Vast);
  const generate = useGenerateEndpointMutation();
  const { can } = useShigoPermissions();
  const canGenerate =
    can(ShigoPermission.SupplyEndpointGeneratorGenerate) ||
    (Boolean(search.publisherId) && can(ShigoPermission.SupplyPublishersEndpointGeneratorGenerate));
  const resolvedPublisher =
    selectedPublisher?.id === publisherId
      ? selectedPublisher
      : publishers.data?.content.find(({ id }) => id === publisherId);
  const resolvedPlacement =
    selectedPlacement?.id === placementId
      ? selectedPlacement
      : placements.data?.content.find(({ id }) => id === placementId);
  const publisherOptions = mergeOptions(resolvedPublisher, publishers.data?.content);
  const placementOptions = mergeOptions(resolvedPlacement, placements.data?.content);
  const exchangeVast =
    adServerType === EndpointAdServerType.Exchange &&
    integrationType === EndpointIntegrationType.Vast;
  const endpoint = exchangeVast ? projectGeneratedEndpoint(fullEndpoint, projection) : fullEndpoint;

  function clearGeneratedEndpoint() {
    setFullEndpoint('');
    setMacroDialogOpen(false);
  }

  async function submit(values: EndpointGeneratorFormValue) {
    if (!canGenerate) return;
    if (!resolvedPublisher?.secondaryId) {
      form.setError('publisherId', {
        message: dashboardT('endpointGenerator.validation.publisher'),
      });
      return;
    }
    if (!resolvedPlacement?.secondaryId) {
      form.setError('placementId', {
        message: dashboardT('endpointGenerator.validation.placement'),
      });
      return;
    }
    try {
      const result = await generate.mutateAsync({
        integrationType: values.integrationType,
        payload: {
          pid: resolvedPublisher.secondaryId,
          placementId: resolvedPlacement.secondaryId,
          ...(resolvedPlacement.deliveryType
            ? { categoryType: resolvedPlacement.deliveryType }
            : {}),
          adServerType: values.adServerType,
          region: values.region,
          audio: values.integrationType === EndpointIntegrationType.Vast && values.audio,
        },
      });
      setFullEndpoint(result);
      toast.success(dashboardT('endpointGenerator.generated'));
    } catch (error) {
      showRequestError(error, dashboardT('endpointGenerator.generateError'));
    }
  }

  return (
    <main className="page-gutter min-w-0 flex-1 py-4 md:py-6">
      <div className="mx-auto w-full max-w-[1920px] space-y-6">
        <div>
          <h1 className="text-2xl font-semibold">{dashboardT('endpointGenerator.title')}</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {dashboardT('endpointGenerator.description')}
          </p>
        </div>
        <Form {...form}>
          <form
            className="grid min-w-0 gap-6 xl:grid-cols-[420px_minmax(0,1fr)]"
            onSubmit={(event) => void form.handleSubmit(submit)(event)}
            noValidate
          >
            <Card className="h-fit">
              <CardHeader className="border-border border-b px-5 py-4">
                <CardTitle className="text-base">
                  {dashboardT('endpointGenerator.configuration')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5 p-5">
                <div className="space-y-1.5">
                  <Label>{dashboardT('endpointGenerator.publisher')}</Label>
                  <Controller
                    control={form.control}
                    name="publisherId"
                    render={({ field, fieldState }) => (
                      <>
                        <AutocompleteSingleSelect
                          label={dashboardT('endpointGenerator.publisher')}
                          value={field.value}
                          options={publisherOptions.map(toAutocompleteOption)}
                          onSearchTermChange={setPublisherSearch}
                          searchDebounceMs={650}
                          minSearchLength={1}
                          loading={publishers.isFetching}
                          placeholder={dashboardT('endpointGenerator.searchPublisher')}
                          searchPlaceholder={dashboardT('endpointGenerator.searchPublisher')}
                          emptyMessage={dashboardT('endpointGenerator.noPublishers')}
                          onValueChange={(value) => {
                            const option = publisherOptions.find(({ id }) => id === value);
                            field.onChange(value);
                            setSelectedPublisher(option);
                            form.setValue('placementId', '');
                            form.setValue('deliveryType', undefined);
                            setSelectedPlacement(undefined);
                            clearGeneratedEndpoint();
                          }}
                        />
                        {fieldState.error ? (
                          <p role="alert" className="text-destructive text-xs">
                            {fieldState.error.message}
                          </p>
                        ) : null}
                      </>
                    )}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>{dashboardT('endpointGenerator.placement')}</Label>
                  <Controller
                    control={form.control}
                    name="placementId"
                    render={({ field, fieldState }) => (
                      <>
                        <AutocompleteSingleSelect
                          label={dashboardT('endpointGenerator.placement')}
                          value={field.value}
                          options={placementOptions.map(toAutocompleteOption)}
                          onSearchTermChange={setPlacementSearch}
                          searchDebounceMs={650}
                          minSearchLength={1}
                          loading={placements.isFetching}
                          disabled={!publisherId}
                          placeholder={dashboardT('endpointGenerator.searchPlacement')}
                          searchPlaceholder={dashboardT('endpointGenerator.searchPlacement')}
                          emptyMessage={dashboardT('endpointGenerator.noPlacements')}
                          onValueChange={(value) => {
                            const option = placementOptions.find(({ id }) => id === value);
                            field.onChange(value);
                            setSelectedPlacement(option);
                            form.setValue('deliveryType', option?.deliveryType);
                            clearGeneratedEndpoint();
                          }}
                        />
                        {fieldState.error ? (
                          <p role="alert" className="text-destructive text-xs">
                            {fieldState.error.message}
                          </p>
                        ) : null}
                        {deliveryType ? (
                          <p className="text-muted-foreground text-xs">
                            {dashboardT('endpointGenerator.deliveryType', {
                              value: deliveryType,
                            })}
                          </p>
                        ) : null}
                      </>
                    )}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>{dashboardT('endpointGenerator.adServer')}</Label>
                  <Controller
                    control={form.control}
                    name="adServerType"
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onValueChange={(value) => {
                          field.onChange(value);
                          clearGeneratedEndpoint();
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.values(EndpointAdServerType).map((value) => (
                            <SelectItem key={value} value={value}>
                              {adServerLabels[value]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
                <SegmentedControl
                  label={dashboardT('endpointGenerator.integrationType')}
                  options={[
                    { value: EndpointIntegrationType.Ortb, label: 'oRTB' },
                    { value: EndpointIntegrationType.Vast, label: 'VAST' },
                  ]}
                  value={integrationType}
                  onChange={(value) => {
                    form.setValue('integrationType', value);
                    if (value === EndpointIntegrationType.Ortb) form.setValue('audio', false);
                    clearGeneratedEndpoint();
                  }}
                />
                <SegmentedControl
                  label={dashboardT('endpointGenerator.region')}
                  options={[
                    { value: EndpointRegion.Us, label: 'US' },
                    { value: EndpointRegion.Eu, label: 'EU' },
                  ]}
                  value={region}
                  onChange={(value) => {
                    form.setValue('region', value);
                    clearGeneratedEndpoint();
                  }}
                />
                <Controller
                  control={form.control}
                  name="audio"
                  render={({ field }) => (
                    <label className="flex cursor-pointer items-center gap-2 text-sm">
                      <Checkbox
                        checked={field.value}
                        disabled={integrationType !== EndpointIntegrationType.Vast}
                        onCheckedChange={(checked) => {
                          field.onChange(checked === true);
                          clearGeneratedEndpoint();
                        }}
                      />
                      {dashboardT('endpointGenerator.audio')}
                    </label>
                  )}
                />
                {canGenerate ? (
                  <Button type="submit" className="w-full" disabled={generate.isPending}>
                    <Bolt className="h-4 w-4" />
                    {dashboardT('endpointGenerator.generate')}
                  </Button>
                ) : null}
              </CardContent>
            </Card>

            <Card className="min-w-0">
              <CardHeader className="border-border flex-row items-center justify-between gap-4 space-y-0 border-b px-5 py-4">
                <div className="min-w-0">
                  <CardTitle className="truncate text-base">
                    {deliveryType
                      ? dashboardT('endpointGenerator.outputTitle', { value: deliveryType })
                      : dashboardT('endpointGenerator.output')}
                  </CardTitle>
                </div>
                {endpoint ? (
                  <div className="flex shrink-0 items-center gap-2">
                    {integrationType === EndpointIntegrationType.Vast &&
                    adServerType !== EndpointAdServerType.Exchange ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={macros.isLoading}
                        onClick={() => {
                          setMacroDialogOpen(true);
                        }}
                      >
                        <SlidersHorizontal className="h-4 w-4" />
                        {dashboardT('endpointGenerator.changeMacros')}
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      aria-label={dashboardT('endpointGenerator.copy')}
                      onClick={() => {
                        void copyToClipboard(endpoint, {
                          successMessage: dashboardT('endpointGenerator.copied'),
                        });
                      }}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                ) : null}
              </CardHeader>
              <CardContent className="min-h-[420px] p-5">
                {exchangeVast && fullEndpoint ? (
                  <div className="mb-4 flex flex-wrap gap-2">
                    {Object.values(EndpointProjection).map((value) => (
                      <Button
                        key={value}
                        type="button"
                        size="sm"
                        variant={projection === value ? 'default' : 'outline'}
                        onClick={() => {
                          setProjection(value);
                        }}
                      >
                        {dashboardT(projectionLabelKeys[value])}
                      </Button>
                    ))}
                  </div>
                ) : null}
                {renderEndpointOutput(generate.isPending, endpoint)}
              </CardContent>
            </Card>
          </form>
        </Form>
      </div>
      {macroDialogOpen ? (
        <EndpointMacroDialog
          endpoint={fullEndpoint}
          macros={macros.data ?? []}
          onClose={() => {
            setMacroDialogOpen(false);
          }}
          onApply={(nextEndpoint) => {
            setFullEndpoint(nextEndpoint);
            setMacroDialogOpen(false);
          }}
        />
      ) : null}
    </main>
  );
}

function SegmentedControl<TValue extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: Array<{ value: TValue; label: string }>;
  value: TValue;
  onChange: (value: TValue) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="bg-muted grid grid-cols-2 rounded-md p-1">
        {options.map((option) => (
          <Button
            key={option.value}
            type="button"
            size="sm"
            variant={value === option.value ? 'outline' : 'ghost'}
            className={value === option.value ? 'bg-card' : ''}
            onClick={() => {
              onChange(option.value);
            }}
          >
            {option.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

function renderEndpointOutput(loading: boolean, endpoint: string) {
  if (loading) {
    return (
      <div className="space-y-3" aria-label={dashboardT('endpointGenerator.loading')}>
        <Skeleton className="h-4 w-4/5" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/5" />
      </div>
    );
  }
  if (endpoint) {
    return (
      <pre className="bg-muted/35 min-h-[180px] rounded-md border p-4 font-mono text-sm break-all whitespace-pre-wrap">
        {endpoint}
      </pre>
    );
  }
  return (
    <div className="text-muted-foreground flex min-h-[360px] items-center justify-center text-center text-sm">
      <p className="max-w-sm">{dashboardT('endpointGenerator.emptyOutput')}</p>
    </div>
  );
}

function mergeOptions(
  selected: EndpointLookupOption | undefined,
  options: EndpointLookupOption[] | undefined,
) {
  const merged = selected ? [selected, ...(options ?? [])] : (options ?? []);
  return [...new Map(merged.map((option) => [option.id, option])).values()];
}

function toAutocompleteOption(option: EndpointLookupOption) {
  return { id: option.id, label: endpointLookupLabel(option) };
}
